import {
    ArtifactId,
    AuditId,
    ExpeditionId,
    FactProcessId,
    FactSessionId,
    FindingId,
    ProjectId,
    SpecId,
    SpecVerificationResultId,
    TestObjectId,
    TestRunId,
    UserId
} from "@webmate-web-core/shared/types/ids";
import {ArtifactAssociation, ArtifactType} from "@webmate-web-core/shared/types/artifact";
import {FindingSeverity, ObservationTarget} from "@webmate-web-core/shared/types/testmgmt";
import {DateTime, Progress, UserMessage} from "@webmate-web-core/shared/types/base-types";

export enum FactGenerationMode {
    Query = "query",
    Streaming = "streaming",
    Watch = "watch"
}

export interface FactRequestParams {
    value: Record<string, any>;
}

export interface FactType {
    artifactType?: string;
    findingType?: string;
}

export interface FactRequest {
    factType: FactType;
    params?: FactRequestParams;
}

export interface FactRequestScope {}
export interface ArtifactScope extends FactRequestScope {
    artifactId: ArtifactId;
}
export interface BrowserSessionScope extends FactRequestScope {
    type: 'BrowserSessionScope';
    browserSessionId: ExpeditionId;
}

export interface ScopedFactRequest {
   request: FactRequest;
   scope: FactRequestScope;
}

export type FactSessionState = 'WaitingForCreation' | 'Running' | 'Finished' | 'Terminated' | 'Failed';
export type FactProcessState = 'Unassigned' | 'WaitingForDependencies' | 'Running' | 'Terminated' | 'Failed';

export interface FactId {
    artifactId?: ArtifactId;
    findingId?: FindingId;
    specVerificationResultId?: SpecVerificationResultId;
}

export interface FactProcessProgress {
    progress?: Progress;
    message?: string;
    userMessage?: UserMessage;
}

export interface FactProcessWarning {
    occurredAt: DateTime;
    message: string;
    relatedFact?: FactId;
    userMessage?: UserMessage;
}

export interface FactProcessInfo {
    id: FactProcessId;
    state: FactProcessState;
    goal: ScopedFactRequest;
    associations: ArtifactAssociation[];
    mode: FactGenerationMode;
    dependencies: FactProcessId[];
    factProcessProgress: FactProcessProgress;
    statusInfo?: Record<string, unknown>;
    name?: string;
    warnings: FactProcessWarning[];
}

export type SpecVerificationOutcome = 'passed' | 'failed' | 'inconclusive' | 'notApplicable';

export interface SpecVerificationResultInfo {
    id: SpecVerificationResultId;
    projectId: ProjectId;
    testRunId: TestRunId;
    specId: SpecId;
    specVersion: number;
    checkerConfigId: string;
    testObjectId: TestObjectId;
    verifierKey: string;
    verifierVersion: string;
    outcome: SpecVerificationOutcome;
    observationTarget: ObservationTarget;
    createdAt: DateTime;
}

export interface FactArtifactInfo {
    id: ArtifactId;
    artifactType: ArtifactType;
    projectId: ProjectId;
    creationTime: DateTime;
    endTime: DateTime | null;
    associations: ArtifactAssociation[];
    version: number;
}

export interface FactFindingInfo {
    id: FindingId;
    version: number;
    creationTime: DateTime;
    creator: UserId;
    projectId: ProjectId;
    title: string | null;
    testRunId: TestRunId | null;
    auditId: AuditId | null;
    resultType: string;
    severity: FindingSeverity;
    observationTargets: ObservationTarget[];
    affectedSpecs: SpecId[];
    lastUpdater: UserId;
    lastUpdated: DateTime;
    lastUpdateAction: string;
    associatedArtifacts: ArtifactId[];
}

export type ArtifactFactInfo = {artifactOrFinding: {artifact: FactArtifactInfo}};
export type FindingFactInfo = {artifactOrFinding: {finding: FactFindingInfo}};
export type SpecVerificationFactInfo = {artifactOrFinding: {specVerification: SpecVerificationResultInfo}};
export type FactInfo = ArtifactFactInfo | FindingFactInfo | SpecVerificationFactInfo;

export interface FactSessionFactStreamReady {
    type: 'ready';
    factSessionId: FactSessionId;
}

export interface FactSessionFactsAdded {
    type: 'factsAdded';
    factSessionId: FactSessionId;
    facts: FactInfo[];
}

export type FactSessionFactStreamMessage = FactSessionFactStreamReady | FactSessionFactsAdded;

export interface FactSessionInfo {
    factSessionId: FactSessionId;
    facts: FactId[];
    state: FactSessionState;
    creationTime: DateTime;
    creator: UserId;
    generationMode: FactGenerationMode;
    processInfo: Record<FactProcessId, FactProcessInfo>;
    projectId: ProjectId;
    goals: ScopedFactRequest[];
    associations: ArtifactAssociation[];
}

export interface FactSessionUpdatedEvent {
    eventType: string;
    projectId: ProjectId;
    info: FactSessionInfo;
}

export function isFindingFactId(factId: FactId): factId is FactId & {findingId: FindingId} {
    return !!factId.findingId;
}

export function isFindingFactInfo(factInfo: FactInfo): factInfo is FindingFactInfo {
    return 'finding' in factInfo.artifactOrFinding;
}

export function isFactSessionFactStreamReady(
    message: FactSessionFactStreamMessage
): message is FactSessionFactStreamReady {
    return message.type === 'ready';
}

export function isFactSessionFactsAdded(
    message: FactSessionFactStreamMessage
): message is FactSessionFactsAdded {
    return message.type === 'factsAdded';
}

export const WellKnownFactEvents = {
    FactSessionUpdated: 'Fact.FactSession.Updated',
}

export const FactSessionStates = {
    WaitingForCreation: 'WaitingForCreation',
    Running: 'Running',
    Finished: 'Finished',
    Terminated: 'Terminated',
    Failed: 'Failed',
}
